/**
 * custom-pickup-ui.js
 * CCC custom feature — unified pickup availability block.
 *
 * Drives the <ccc-pickup-availability> custom element.
 * Updates the pickup status dynamically when the selected variant changes.
 *
 * Logic:
 *   Coffee + Upcoming Roast variant
 *     → Roast day before 7am:      ✅ Ready for pickup later today — order by 7:00am
 *     → Otherwise:                  ❌ Ready after [roast date]
 *   In-stock variant               → ✅ Usually ready in 2 hours
 *   Preorder (OOS + continue)      → ❌ Ready for pickup around [dispatch range]
 *   Sold out                       → ❌ Unavailable for pickup
 */

import { ThemeEvents } from '@theme/events';
import { cachedRoastDate } from '@theme/custom-roast-manager';
import { getPickupMessage, getRestockEta, getDispatchKey } from '@theme/custom-dispatch-manager';

class CccPickupAvailability extends HTMLElement {
  connectedCallback() {
    this._isCoffee    = this.hasAttribute('data-is-coffee');
    this._productType = this.dataset.productType || '';
    this._vendor      = this.dataset.vendor || '';
    this._variants    = this._parseVariants();

    // Cache DOM references
    this._iconAvailable   = this.querySelector('[data-ccpa-icon-available]');
    this._iconUnavailable = this.querySelector('[data-ccpa-icon-unavailable]');
    this._statusEl        = this.querySelector('[data-ccpa-status]');
    this._titleEl         = this.querySelector('[data-ccpa-title]');

    // Settings from Liquid (baked into the initial HTML)
    this._pickupTime    = this._statusEl?.querySelector('.ccpa-time--available')?.textContent?.trim() || 'Usually ready in 2 hours';
    this._soldOutMsg    = 'Unavailable for pickup';

    // The showroom is closed Sat/Sun (Sydney time), so "ready in 2 hours"
    // would be wrong all weekend — those orders are ready from Monday morning.
    const sydneyDay = new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Sydney', weekday: 'short' }).format(new Date());
    if (sydneyDay === 'Sat' || sydneyDay === 'Sun') {
      this._pickupTime = this.dataset.pickupTimeWeekend || 'We open 7:30am Monday — weekend orders are usually ready to collect by 10am';
      const statusText = this._statusEl?.querySelector('[data-ccpa-status-text]');
      if (statusText && this._statusEl.classList.contains('ccpa-status--available')) {
        statusText.textContent = this._pickupTime;
      }
    }

    // Initial render from JS to fill roast date if needed
    const initialId = this._getInitialVariantId();
    if (initialId) this._update(initialId);

    // Listen for variant changes
    this._boundSelectedHandler = (e) => {
      if (!this._isSameProductContext(e)) return;

      const id = e.detail?.resource?.variantId;
      if (!id) return;

      clearTimeout(this._debounce);
      this._update(id);
    };
    this._boundHandler = (e) => {
      const id = e.detail?.resource?.id;
      if (id) {
        clearTimeout(this._debounce);
        this._debounce = setTimeout(() => this._update(id), 40);
      }
    };
    document.addEventListener(ThemeEvents.variantSelected, this._boundSelectedHandler);
    document.addEventListener(ThemeEvents.variantUpdate, this._boundHandler);
  }

  disconnectedCallback() {
    if (this._boundSelectedHandler) {
      document.removeEventListener(ThemeEvents.variantSelected, this._boundSelectedHandler);
    }

    if (this._boundHandler) {
      document.removeEventListener(ThemeEvents.variantUpdate, this._boundHandler);
    }
  }

  _parseVariants() {
    try {
      const el = this.querySelector('[data-ccpa-variants]');
      return el ? JSON.parse(el.textContent) : [];
    } catch {
      return [];
    }
  }

  _getInitialVariantId() {
    const urlParams = new URLSearchParams(window.location.search);
    const fromUrl = parseInt(urlParams.get('variant'), 10);
    if (fromUrl) return fromUrl;

    const input = document.querySelector('input[name="id"]');
    if (input) return parseInt(input.value, 10);

    return this._variants.length ? this._variants[0].id : null;
  }

  async _update(variantId) {
    const variant = this._variants.find(v => String(v.id) === String(variantId));
    if (!variant) return;

    // Guard against stale async results
    this._pendingId = variantId;

    const isUpcoming = this._isCoffee &&
      variant.title?.toLowerCase().includes('upcoming roast');
    const isPreorder = variant.inventory_quantity <= 0 &&
      variant.inventory_policy === 'continue';
    const isAvailable = variant.available && !isUpcoming && !isPreorder;
    const isSoldOut = !variant.available && !isPreorder;

    // Toggle icons synchronously
    if (isUpcoming || isPreorder || isSoldOut) {
      this._showUnavailable();
    } else {
      this._showAvailable();
    }

    // Update status text
    if (isUpcoming) {
      // Coffee upcoming roast — fetch roast date async
      this._setStatus('unavailable', 'Pickup unavailable today. Ready after next roast date.');
      try {
        const info = await cachedRoastDate();
        if (this._pendingId !== variantId) return; // stale
        if (info) {
          if (info.isToday && info.beforeCutoff) {
            // Roast day before 7am — coffee will be roasted today, pickup later
            this._showAvailable();
            this._setStatus('available', 'Ready for pickup later today — order by 7:00am');
            if (this._titleEl) this._titleEl.textContent = 'Pickup available';
            return; // skip the generic title setter below
          } else {
            this._setStatus('unavailable', `Pickup unavailable today. Ready after <strong>${info.formatted}</strong>.`);
          }
        }
      } catch {
        // Keep fallback text
      }
    } else if (isAvailable) {
      // In-stock — ready for pickup
      this._setStatus('available', this._pickupTime);
    } else if (isPreorder) {
      // Out of stock but purchasable — use dispatch estimate / restock date
      const lookupKey = getDispatchKey(this._productType, this._vendor);
      const restockOptions = { estimatedRestockDate: variant.estimated_restock_date || null };
      const msg = getPickupMessage(lookupKey, restockOptions);
      this._setStatus('unavailable', `Pickup unavailable today. ${msg}.`);
    } else {
      // Sold out and not oversellable. Pickup stays unavailable either way —
      // the restock date only adds when it is worth checking back.
      const isTracked = Boolean(variant.inventory_management);
      const restockEta = isTracked ? getRestockEta(variant.estimated_restock_date) : null;
      this._setStatus(
        'unavailable',
        restockEta ? `${this._soldOutMsg} · ${restockEta.phrase}` : this._soldOutMsg
      );
    }

    // Update header title
    if (this._titleEl) {
      this._titleEl.textContent = (isAvailable && !isUpcoming)
        ? 'Pickup available'
        : 'Pickup availability';
    }
  }

  _showAvailable() {
    if (this._iconAvailable) this._iconAvailable.hidden = false;
    if (this._iconUnavailable) this._iconUnavailable.hidden = true;
  }

  _showUnavailable() {
    if (this._iconAvailable) this._iconAvailable.hidden = true;
    if (this._iconUnavailable) this._iconUnavailable.hidden = false;
  }

  _setStatus(type, html) {
    if (!this._statusEl) return;
    const cls = type === 'available' ? 'ccpa-time--available' : 'ccpa-time--unavailable';
    this._statusEl.classList.toggle('ccpa-status--available', type === 'available');
    this._statusEl.classList.toggle('ccpa-status--unavailable', type !== 'available');
    this._statusEl.innerHTML = `<span class="${cls}">${html}</span>`;
  }

  _isSameProductContext(event) {
    if (!(event.target instanceof HTMLElement)) return false;

    const sourceSection = event.target.closest('.shopify-section, dialog');
    const hostSection = this.closest('.shopify-section, dialog');

    return !sourceSection || !hostSection || sourceSection === hostSection;
  }
}

if (!customElements.get('ccc-pickup-availability')) {
  customElements.define('ccc-pickup-availability', CccPickupAvailability);
}
